/**
 * State manager that controls component re-renders (e.g., reviews, product details)
 * by updating their version keys via reactive watchers.
 * Forced rerender will only happen once to force lazy-hydrated components to hydrate early by rerendering the components
 */
import { ref, watch, shallowRef, type WatchSource, type WatchHandle } from 'vue';

export default function useTriggerComponentRefreshOnce() {
  // Numeric version used as the Vue :key to force a full re-render when changed
  const forcedRerender = ref<number>(-Infinity);
  // Store the active watch cleanup function to allow manual teardown (e.g. after hydration)
  const unWatchComponentRefresh = shallowRef<WatchHandle>();

  // Sets up a one-time watcher that updates componentVersion to trigger a refresh
  const setComponentRefreshWatcher = (watcherSource: WatchSource) => {
    unWatchComponentRefresh.value = watch(
      watcherSource,
      () => {
        // reduce INP by hydrating / rerendering comp after the browser paints
        setTimeout(() => {
          forcedRerender.value = Infinity;
        }, 0);
      },
      { once: true },
    );
  };

  return {
    forcedRerender,
    unWatchComponentRefresh,
    setComponentRefreshWatcher,
  };
}
